import SectionHeader from "@/components/Home/v2/shared/SectionHeader";
import Text from "@/components/Home/v2/shared/Text";
import { useRouter } from "next/router";

export type ThreePillarCard = {
  label: string;
  /** CSS gradient string used as gradient text on the card heading. */
  labelGradient: string;
  /** CSS gradient for the illustration outer box background. */
  illustrationGradient?: string;
  title: string;
  description: string;
  ctaText?: string;
  /** Optional image URL — when set, renders instead of the hardcoded slot component. */
  image?: string;
  /** @deprecated No longer used — kept for backwards compatibility. */
  badgeText?: string;
};

export type ThreePillarCardsSectionData = {
  title: string;
  subtitle: string;
  /** @deprecated No longer rendered — kept for backwards compatibility. */
  aiBarPlaceholder?: string;
  cards: [ThreePillarCard, ThreePillarCard, ThreePillarCard];
};

const ThreePillarCardsSection = ({
  title,
  subtitle,
  cards,
}: ThreePillarCardsSectionData) => {
  const router = useRouter();
  const signupHref =
    router.locale && router.locale !== router.defaultLocale
      ? `/${router.locale}/signup`
      : "/signup";

  return (
    <section className="relative w-full overflow-hidden bg-black">
      <div className="container mx-auto px-4 pt-16 pb-16 sm:px-6 md:px-8 md:pt-24 md:pb-24 lg:px-12">
        <SectionHeader
          title={title}
          subtitle={subtitle}
          variant="dark-lime"
          align="center"
        />

        {/* Cards grid */}
        <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 md:mt-16 md:grid-cols-3 md:gap-6">
          {cards.map((card, cardIdx) => (
            <div
              key={card.title}
              className="flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-sm"
            >
              {/* Text block */}
              <div className="flex flex-col p-6 md:p-7">
                {/* Eyebrow — dot + label */}
                <div className="flex items-center gap-2">
                  <span className="inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#D4F75E]" />
                  <Text
                    as="span"
                    variant="eyebrow"
                    className="text-[#D4F75E]"
                    style={{ letterSpacing: "0.6px" }}
                  >
                    {card.label}
                  </Text>
                </div>

                <Text
                  as="h3"
                  variant="title-md"
                  className="mt-2 font-bold text-white"
                >
                  {card.title}
                </Text>

                <Text
                  as="p"
                  variant="body-md"
                  className="mt-3 text-white/70"
                >
                  {card.description}
                </Text>

                {card.ctaText && (
                  <a
                    href={signupHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-inter mt-5 inline-block text-[13px] font-semibold text-[#D4F75E] hover:underline"
                  >
                    {card.ctaText}
                  </a>
                )}
              </div>

              {/* Card illustration — image when provided, else neutral placeholder */}
              {card.image ? (
                <div className="mx-4 mb-4 mt-auto overflow-hidden rounded-2xl md:mx-5 md:mb-5">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-auto block"
                  />
                </div>
              ) : (
                <div className="mx-4 mb-4 mt-auto overflow-hidden rounded-2xl md:mx-5 md:mb-5">
                  <div className="aspect-[4/3] w-full rounded-2xl" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ThreePillarCardsSection;
